<template>
  <DashboardCard :title="t('informations.title')">
    <div class="flex grow px-6">
      <div>
        <template v-if="infos.bank">
          <div class="text-xs text-medium-emphasis mt-5">{{ t("common.bank") }}</div>
          <div class="text-base">{{ infos.bank }}</div>
        </template>
        <template v-if="infos.iban">
          <div class="text-xs text-medium-emphasis mt-5">{{ t("common.iban") }}</div>
          <div class="flex items-center flex-wrap gap-2">
            <span class="text-base">{{ infos.iban }}</span>
            <v-btn
              v-if="
                accountType &&
                getProviderType(accountType) === 'proAccount' &&
                accountState?.status !== 'refused'
              "
              v-tooltip="t('informations.downloadBankDetails')"
              size="small"
              append-icon="mdi-download"
              :disabled="!infos.iban"
              @click="openInNewTab('bankDetails', { accountId })"
            >
              {{ t("informations.downloadRib") }}
            </v-btn>
            <v-chip
              v-if="
                accountType === 'treezor' &&
                (accountState?.status !== 'synced' || !account.supervisorOrgId)
              "
              v-tooltip="
                accountState?.status === 'refused'
                  ? undefined
                  : t('informations.frozenAccountTooltip')
              "
              color="warning"
              size="small"
              prepend-icon="mdi-alert"
              :append-icon="
                accountState?.status === 'refused' ? undefined : 'mdi-information-outline'
              "
            >
              {{ t("informations.frozenAccount") }}
            </v-chip>
          </div>
        </template>
        <template
          v-if="
            accountType === 'treezor' && accountState?.status !== 'synced' && accountStateUi.message
          "
        >
          <div class="text-xs text-medium-emphasis mt-5">{{ t("common.status") }}</div>
          <div class="text-base">{{ accountStateUi.message }}</div>
        </template>
        <template v-if="account.accountType === 'treezor' && account.contractEnvelope">
          <div class="text-xs text-medium-emphasis mt-5">
            {{ t("account.tripartiteContract.title") }}
          </div>
          <div v-if="account.contractEnvelope.status !== 'completed'" class="text-sm">
            {{ t("account.tripartiteContract.waitingForSupervisorOrg") }}
          </div>
          <v-btn
            v-if="
              account.contractEnvelope.deliverable.status === 'generated' &&
              user.hasRole(accountManagerRoles, { account })
            "
            class="mt-1"
            size="small"
            append-icon="mdi-download"
            data-testid="account-tripartite-contract-download-button"
            @click="openInNewTab('contract', { accountId })"
            >{{ t("account.tripartiteContract.download") }}</v-btn
          >
        </template>
        <template v-if="infos.accountName">
          <div class="text-xs text-medium-emphasis mt-5">{{ t("common.accountName") }}</div>
          <div class="text-base">{{ infos.accountName }}</div>
        </template>
        <template v-if="infos.accountHolderName">
          <div class="text-xs text-medium-emphasis mt-5">
            {{ t("common.accountHolderName") }}
          </div>
          <div class="text-base">{{ infos.accountHolderName }}</div>
        </template>
        <div
          v-if="
            accountType &&
            getProviderType(accountType) === 'proAccount' &&
            procedureCollectiveStatus !== undefined &&
            procedureCollectiveStatus !== null
          "
          class="mt-1"
        >
          <v-tooltip>
            <template #activator="{ props: tooltipProps }">
              <v-chip
                v-bind="tooltipProps"
                :color="procedureCollectiveStatus ? 'warning' : 'success'"
                size="small"
                :prepend-icon="procedureCollectiveStatus ? 'mdi-alert' : 'mdi-check-circle'"
              >
                {{
                  procedureCollectiveStatus
                    ? t("informations.procedureCollective")
                    : t("transfers.files.issuerNoProcedureCollective")
                }}
              </v-chip>
            </template>
            <span>{{
              procedureCollectiveStatus
                ? t("informations.accountHolderProcedureCollectiveTooltip")
                : t("informations.accountHolderNoProcedureCollectiveTooltip")
            }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </DashboardCard>
</template>

<script lang="ts" setup>
import { useAccountState } from "@/composables/useAccountState";
import { useBankAccount } from "@/composables/useBankAccount";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { useUserStore } from "@/store";
import { getProviderType, type Account } from "@aircap/common/account";
import { accountManagerRoles } from "@aircap/common/auth";
import { hasOngoingCollectiveProcedure } from "@aircap/common/pappers";
import { computed, toRef } from "vue";
import DashboardCard from "./DashboardCard.vue";

const props = defineProps<{
  account: Account & { readonly id: string };
  accountId: string;
}>();

const { t } = useI18n();
const user = useUserStore();
const openInNewTab = useOpenInNewTab();
const { accountType, bridgeAccount, powensAccount, gclAccount } = useBankAccount(
  toRef(props.accountId),
);

const { accountState, accountStateUi } = useAccountState(toRef(props, "account"));

const procedureCollectiveStatus = computed(() => {
  if (!props.account.pappers?.fiche) return undefined;
  return hasOngoingCollectiveProcedure(props.account.pappers.fiche);
});

const infos = computed(() => {
  switch (accountType.value) {
    case "bridge": {
      return {
        bank: props.account.bank?.name,
        iban: bridgeAccount.value?.details.iban,
        accountName: bridgeAccount.value?.details.name,
      };
    }
    case "swan": {
      return {
        bank: "Aircap",
        iban: props.account.swan_account?.IBAN,
        accountName: props.account.swan_account?.name,
        accountHolderName: props.account.swan_account_holder?.name,
      };
    }
    case "powens": {
      return {
        bank: props.account.bank?.name,
        iban: powensAccount.value?.iban,
        accountName: powensAccount.value?.name,
      };
    }
    case "gcl": {
      return {
        bank: props.account.bank?.name,
        iban: gclAccount.value?.metadata.iban,
        accountName: gclAccount.value?.metadata.name,
      };
    }
    case "treezor": {
      return {
        bank: "Aircap",
        iban: props.account.treezorWallet?.iban,
        accountName: props.account.treezorCompany?.legalName,
      };
    }
    case undefined: {
      return {};
    }
  }
});
</script>
